import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, ImagePlus, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { PRODUCT_TYPES, SERVICES } from "../lib/content";
import { PageHero } from "../components/site/Layout";
import { Sparkle } from "../components/site/Decor";
import { useAuth } from "../context/AuthContext";
import { useSite } from "../context/SiteContext";

const field = "w-full rounded-xl border border-[#EBD08A] bg-white px-4 py-3.5 text-base outline-none transition-shadow duration-200 focus:border-[#FF1F8E] focus:shadow-[0_0_0_3px_rgba(255,31,142,.15)]";
const Label = ({ children, req }) => <span className="mb-1.5 block text-sm font-semibold">{children}{req && <span className="text-[#FF1F8E]"> *</span>}</span>;

const initialType = (t) => {
  const s = SERVICES.find((x) => x.title === t);
  return s ? { embroidery: "Hat", tees: "T-shirt", spirit: "School spirit / Team apparel", seasonal: "Seasonal / Awareness design", bulk: "Bulk / Special order" }[s.slug] : "";
};

const Success = ({ customer }) => (
  <div className="stitch-box relative mx-auto max-w-2xl rounded-[2rem] bg-[#FFF4F8] p-10 text-center" data-testid="order-success">
    <Sparkle className="absolute right-8 top-8" size={22} />
    <CheckCircle2 className="mx-auto h-14 w-14 text-[#FF1F8E]" />
    <p className="mt-4 font-script text-5xl text-[#FF1F8E]">Thank you!</p>
    <h2 className="mt-2 font-display text-2xl font-bold sm:text-3xl">Your request has been received.</h2>
    <p className="mt-4 text-[#0E0B0D]/70">We'll review your idea and reach out by phone, text or email with a quote. A confirmation email is on its way to your inbox.</p>
    {customer
      ? <Link to="/account" className="btn-pink mt-8" data-testid="order-success-myorders-btn">Track it in My Orders</Link>
      : <Link to="/" className="btn-pink mt-8" data-testid="order-success-home-btn">Back to Home</Link>}
  </div>
);

export default function OrderRequest() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", phone: "", email: "", product_type: initialType(params.get("type")), quantity: 1, sizes_colors: "", design_idea: "", needed_by: "", payment_method: "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const { user } = useAuth();
  const { site } = useSite();
  const PAYMENTS = site.payments;
  const customer = !!user && user.role === "customer";

  useEffect(() => {
    if (customer) setForm((f) => ({ ...f, name: f.name || user.name, email: f.email || user.email }));
  }, [customer, user]);

  const addFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    const next = [...files, ...picked].slice(0, 3);
    if (files.length + picked.length > 3) toast.error("You can attach up to 3 images");
    setFiles(next);
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.product_type || !form.payment_method) return toast.error("Please choose a product type and payment method");
    setBusy(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    files.forEach((f) => fd.append("images", f));
    try {
      await api.post("/orders", fd);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHero eyebrow="Custom order request" title="Let's create" script="something special" testid="order-hero">
        {site.order_form_intro}
      </PageHero>
      <section className="mx-auto max-w-4xl px-4 py-14 sm:px-6">
        {!done && user === false && (
          <p className="mb-5 rounded-2xl bg-[#FFF4F8] px-5 py-3 text-sm" data-testid="order-signin-hint">
            Want to track your order? <Link to="/account/login?next=/order" className="font-semibold text-[#FF1F8E]">Sign in or create an account</Link> first, or just send as a guest.
          </p>
        )}
        {done ? <Success customer={customer} /> : (
          <form onSubmit={submit} className="space-y-6 rounded-[2rem] bg-white p-6 shadow-[0_30px_80px_-40px_rgba(255,31,142,.35)] ring-1 ring-[#EBD08A] sm:p-10" data-testid="order-form">
            <div className="grid gap-5 sm:grid-cols-2">
              <label className="sm:col-span-2"><Label req>Full name</Label><input required className={field} value={form.name} onChange={set("name")} data-testid="order-name-input" /></label>
              <label><Label req>Phone</Label><input required type="tel" className={field} value={form.phone} onChange={set("phone")} data-testid="order-phone-input" /></label>
              <label><Label req>Email</Label><input required type="email" className={field} value={form.email} onChange={set("email")} data-testid="order-email-input" /></label>
              <label><Label req>Product type</Label>
                <select required className={field} value={form.product_type} onChange={set("product_type")} data-testid="order-product-select">
                  <option value="">Choose one…</option>
                  {PRODUCT_TYPES.map((p) => <option key={p}>{p}</option>)}
                </select>
              </label>
              <label><Label req>Quantity</Label><input required type="number" min="1" className={field} value={form.quantity} onChange={set("quantity")} data-testid="order-quantity-input" /></label>
              <label className="sm:col-span-2"><Label>Sizes & colors</Label><input className={field} placeholder="e.g. 3 Youth M, 2 Adult L, hot pink thread on black" value={form.sizes_colors} onChange={set("sizes_colors")} data-testid="order-sizes-input" /></label>
              <label className="sm:col-span-2"><Label req>Design idea or text</Label><textarea required rows={5} className={field} placeholder="Names, wording, fonts, colors, placement… tell us everything!" value={form.design_idea} onChange={set("design_idea")} data-testid="order-design-input" /></label>
              <label><Label>Date needed by</Label><input type="date" className={field} value={form.needed_by} onChange={set("needed_by")} data-testid="order-date-input" /></label>
              <div><Label req>Preferred payment</Label>
                <div className="grid grid-cols-2 gap-2" data-testid="order-payment-options">
                  {PAYMENTS.map((p) => (
                    <button type="button" key={p} onClick={() => setForm({ ...form, payment_method: p })} data-testid={`order-payment-${p.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}
                      className={`rounded-xl px-3 py-3 text-sm font-semibold transition-colors duration-200 ${PAYMENTS.length % 2 ? "last:col-span-2" : ""} ${form.payment_method === p ? "bg-[#FF1F8E] text-white" : "border border-[#EBD08A] hover:bg-[#FFF4F8]"}`}>{p}</button>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <Label>Reference images (up to 3)</Label>
              <div className="flex flex-wrap gap-3">
                {files.map((f, i) => (
                  <div key={i} className="relative h-24 w-24 overflow-hidden rounded-xl ring-1 ring-[#EBD08A]" data-testid={`order-image-preview-${i}`}>
                    <img src={URL.createObjectURL(f)} alt="" className="h-full w-full object-cover" />
                    <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="absolute right-1 top-1 rounded-full bg-black/70 p-1 text-white" data-testid={`order-image-remove-${i}`}><X className="h-3 w-3" /></button>
                  </div>
                ))}
                {files.length < 3 && (
                  <label className="grid h-24 w-24 cursor-pointer place-items-center rounded-xl border-2 border-dashed border-[#D4A537] text-[#D4A537] transition-colors hover:bg-[#FFF4F8]" data-testid="order-image-add">
                    <ImagePlus className="h-6 w-6" />
                    <input type="file" accept="image/*" multiple className="hidden" onChange={addFiles} data-testid="order-image-input" />
                  </label>
                )}
              </div>
            </div>
            <button type="submit" disabled={busy} className="btn-pink w-full !py-4 text-lg disabled:opacity-60" data-testid="order-submit-btn">
              {busy ? <><Loader2 className="h-5 w-5 animate-spin" /> Sending…</> : "Send My Request"}
            </button>
            <p className="text-center text-xs text-[#0E0B0D]/55">Your order is confirmed once payment is received. You'll get a confirmation email right after sending.</p>
          </form>
        )}
      </section>
    </>
  );
}
